import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Users, ArrowLeft, Save, Send, RotateCcw, History } from "lucide-react";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    APPRAISALS,
    APPRAISAL_RM_REVIEW,
} from "../../constants/api";

import StatusPill from "../../components/appraisal/StatusPill";
import useAppraisalRole from "../../hooks/useAppraisalRole";
import KPIGoalRow from "../../components/appraisal/KPIGoalRow";
import AuditTimeline from "../../components/appraisal/AuditTimeline";
import SendBackModal from "../../components/appraisal/SendBackModal";

const reviewableStatuses = new Set(["submitted_to_rm", "sent_back_by_hr"]);

const TeamReviewDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch, update, create } = useApiMethods();
    const { isRM } = useAppraisalRole();

    const [appraisal, setAppraisal] = useState(null);
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmApprove, setConfirmApprove] = useState(false);
    const [sendBackOpen, setSendBackOpen] = useState(false);
    const [showHistory, setShowHistory] = useState(true);
    const [historyKey, setHistoryKey] = useState(0);

    const loadAppraisal = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISALS, { id });
            setAppraisal(res?.data || null);
            setGoals(res?.data?.goals || []);
        } catch (_) {
            setAppraisal(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { loadAppraisal(); }, [loadAppraisal]);

    if (loading) {
        return (
            <ContainerCard scrollable>
                <div className="text-center py-12 text-muted-foreground">Loading…</div>
            </ContainerCard>
        );
    }

    if (!appraisal) {
        return (
            <ContainerCard scrollable>
                <ModuleTitleCard Icon={Users} title="Appraisal Not Found" />
                <div className="px-3">
                    <Button variant="outline" size="sm" onClick={() => navigate("/appraisal/team-review")}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    const canReview = isRM && reviewableStatuses.has(appraisal.status) && !appraisal.is_locked;
    const allGoalsRated = goals.every((g) => g.rm_rating !== null && g.rm_rating !== undefined);

    const handleGoalChange = (updated) => {
        setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    };

    const buildPayload = () => ({
        id,
        goals: goals.map((g) => ({
            id: g.id,
            rm_rating: g.rm_rating,
            rm_comment: g.rm_comment,
        })),
    });

    const saveDraft = async () => {
        setSaving(true);
        try {
            const res = await update(APPRAISALS, buildPayload());
            if (res?.status === "success") {
                ToastMsg("success", "Review draft saved");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
            } else {
                ToastMsg("error", res?.message || "Failed to save");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const approve = async () => {
        if (!allGoalsRated) {
            ToastMsg("error", "Please rate all KPIs before approving");
            setConfirmApprove(false);
            return;
        }
        try {
            const res = await create(APPRAISAL_RM_REVIEW, {
                action: "approve",
                comment: "RM approved and forwarded to HR.",
                ...buildPayload(),
            });
            if (res?.status === "success") {
                ToastMsg("success", "Approved and sent to HR");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to approve");
            }
        } catch (_) {
            ToastMsg("error", "Failed to approve");
        }
        setConfirmApprove(false);
    };

    const sendBack = async (comment) => {
        try {
            const res = await create(APPRAISAL_RM_REVIEW, {
                action: "send_back",
                comment,
                ...buildPayload(),
            });
            if (res?.status === "success") {
                ToastMsg("success", "Sent back to employee");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to send back");
            }
        } catch (_) {
            ToastMsg("error", "Failed to send back");
        }
        setSendBackOpen(false);
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Users}
                title={appraisal.employee?.name || "Team Review"}
                subtitle={`${appraisal.employee?.designation || ""} · ${appraisal.cycle_name || ""}`}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/team-review")} className="self-start">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                        <StatusPill value={appraisal.status} />
                        {appraisal.self_submitted_at && (
                            <span className="text-xs text-muted-foreground">
                                Submitted {dayjs(appraisal.self_submitted_at).format("DD MMM YYYY")}
                            </span>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setShowHistory((v) => !v)}>
                            <History className="w-4 h-4 mr-1" /> History
                        </Button>
                    </div>
                </div>

                {showHistory && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                        <h3 className="text-base font-semibold mb-3">Activity History</h3>
                        <AuditTimeline appraisalId={appraisal.id} refreshKey={historyKey} />
                    </div>
                )}

                <div className="space-y-3">
                    {goals.map((g) => (
                        <KPIGoalRow
                            key={g.id}
                            goal={g}
                            role="rm"
                            readOnly={!canReview}
                            onChange={handleGoalChange}
                        />
                    ))}
                </div>

                {canReview && (
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sticky bottom-0 bg-gradient-to-t from-white dark:from-gray-900 from-50% to-transparent pt-3 -mx-3 px-3 pb-1">
                        <Button variant="outline" onClick={saveDraft} disabled={saving}>
                            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Draft"}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setSendBackOpen(true)}
                            className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
                        >
                            <RotateCcw className="w-4 h-4 mr-1" /> Send Back
                        </Button>
                        <Button
                            variant="brand"
                            onClick={() => setConfirmApprove(true)}
                            disabled={!allGoalsRated}
                            title={!allGoalsRated ? "Rate all KPIs first" : undefined}
                        >
                            <Send className="w-4 h-4 mr-1" /> Approve & Forward to HR
                        </Button>
                    </div>
                )}
            </div>

            <ConfirmPopupModal
                open={confirmApprove}
                onClose={() => setConfirmApprove(false)}
                onConfirm={approve}
                title="Approve & Forward"
                description="Forward this appraisal to HR for final approval? Your ratings and comments will be locked."
                type="info"
                confirmLabel="Approve"
            />

            <SendBackModal
                open={sendBackOpen}
                onClose={() => setSendBackOpen(false)}
                onConfirm={sendBack}
                title="Send Back to Employee"
                actor="RM"
            />
        </ContainerCard>
    );
};

export default TeamReviewDetail;
